/*
 * aura-feed.js: the reels swipe feed (docs/socialfeed/05). One vertical scroll-snap list, no page reloads.
 * Liquid renders the first reels as real HTML; this component then reads the static feed JSON
 * (data-index) and adds reels below and above them as you scroll, and only ever rewrites the
 * address bar with replaceState (never pushState, so Back leaves the feed in one step).
 * - Cards are clones of <template data-reel-template> (snippets/reel-card.liquid), so JSON and server
 *   cards share one markup. bodyHtml is sanitized before it's inserted.
 * - Reels above the current one are inserted only when scrolling is idle (scrollend, else 150 ms
 *   without scroll events), and scrollTop is corrected in the same frame, so the view never jumps.
 * - At most 3 videos (previous, current, next) hold a src. Reels more than 20 away become
 *   same-height placeholders, so memory stays flat.
 * - If index.json or a page fails, nothing changes: the server cards and the pager keep working.
 *   The pager is hidden only once the JSON has taken over.
 * - Data source seam: { index(), page(n) }. Only the S3 JSON source exists; spec 16's builds feed
 *   can pass another one.
 * - Desktop: only the video scrolls the feed. A wheel over the post or the empty sides scrolls the
 *   page (or the post first, while it has more to show), so the page never feels trapped.
 * State for tests and styling: [data-live], [data-failed], [data-loading], [data-scrolling],
 * [data-end], [data-view="grid"], [data-current="<handle>"].
 */
(() => {
  if (customElements.get('aura-feed')) return;

  const HAS_SCROLLEND = 'onscrollend' in window;
  const NEAR = 3;         // load the next or previous page this many reels from the edge
  const KEEP = 20;        // reels further than this become placeholders
  const SERVER_PAGE = 6;  // sections/reels-feed.liquid: paginate blog.articles by 6
  const DESKTOP = matchMedia('(min-width: 1024px)'); // section-reels.css: the side-by-side layout
  const SOUND_KEY = 'aura.reels.sound';
  const BAD_TAGS = 'script,iframe,frame,object,embed,base,link,meta,style,form';
  const URL_ATTR = /^(href|src|xlink:href|action|formaction|poster|background)$/;

  const track = (name, props) => { try { window.aura && window.aura.track(name, props); } catch (e) { /* never break the feed */ } };
  const store = {
    get(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, v); } catch (e) { /* private mode */ } }
  };
  const frame = () => new Promise(requestAnimationFrame);

  /* Data source: the static feed on storage (docs/socialfeed/04). */
  function s3Source(indexUrl) {
    let idx = null;
    const pages = new Map();
    const get = (u) => fetch(u, { credentials: 'omit' }).then((r) => {
      if (!r.ok) throw new Error(`feed ${r.status}`);
      return r.json();
    });
    return {
      index() {
        if (!idx) idx = get(indexUrl).catch((e) => { idx = null; throw e; });
        return idx;
      },
      async page(n) {
        if (!pages.has(n)) {
          const i = await this.index();
          const base = new URL(i.base || './', indexUrl);
          pages.set(n, get(new URL(i.pages[n - 1], base)).catch((e) => { pages.delete(n); throw e; }));
        }
        return pages.get(n);
      }
    };
  }

  /* bodyHtml comes from our own feed, but it's still treated as untrusted. */
  function clean(html) {
    const t = document.createElement('template');
    t.innerHTML = html || '';
    t.content.querySelectorAll(BAD_TAGS).forEach((n) => n.remove());
    t.content.querySelectorAll('*').forEach((el) => {
      for (const a of [...el.attributes]) {
        const name = a.name.toLowerCase();
        const value = a.value.replace(/[\s\u0000-\u001f]+/g, '').toLowerCase();
        if (name.startsWith('on') || name === 'srcdoc' || name === 'style' ||
            (URL_ATTR.test(name) && /^(javascript|data|vbscript):/.test(value)) ||
            (name === 'srcset' && /(javascript|data|vbscript):/.test(value))) el.removeAttribute(a.name);
      }
    });
    return t.content;
  }

  class AuraFeed extends HTMLElement {
    connectedCallback() {
      if (this.list) return;
      this.list = this.querySelector('.reel-feed__list');
      this.tpl = this.querySelector('template[data-reel-template]');
      this.pager = this.querySelector('.reel-feed__pager');
      if (!this.list || !this.tpl) return;
      try { this.t = JSON.parse(this.querySelector('[data-reel-strings]').textContent); } catch (e) { this.t = {}; }
      this.base = (this.dataset.blog || '/blogs/reels').replace(/\/$/, '');
      this.items = new Map();
      this.parked = new Map();
      this.loaded = new Set();
      this.loading = new Map();
      this.idleQ = [];
      this.chain = Promise.resolve();
      this.inserting = 0;
      this.sound = store.get(SOUND_KEY) === '1';
      this.gesture = false;
      this.cc = null;
      const conn = navigator.connection || {};
      this.lite = !!conn.saveData || /2g|3g/.test(conn.effectiveType || '');
      this.auto = !this.lite && !matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.source = s3Source(new URL(this.dataset.index || '', location.href).href);

      this.io = new IntersectionObserver((entries) => {
        if (this.dataset.view === 'grid') return;
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio >= 0.6 && e.target !== this.cur) this.setCurrent(e.target);
        }
      }, { root: this.list, threshold: 0.6 });
      this.reels().forEach((li) => this.io.observe(li));

      this.list.addEventListener('scroll', () => this.onScroll(), { passive: true });
      if (HAS_SCROLLEND) this.list.addEventListener('scrollend', () => this.settle());
      this.list.addEventListener('wheel', (e) => this.onWheel(e), { passive: false });
      this.addEventListener('click', (e) => this.onClick(e));
      this.list.addEventListener('playing', (e) => this.mark(e, true), true);
      this.list.addEventListener('pause', (e) => this.mark(e, false), true);
      document.addEventListener('keydown', (e) => this.onKey(e));
      const gesture = () => { this.gesture = true; if (this.sound) this.applySound(); };
      addEventListener('pointerdown', gesture, { once: true, capture: true });
      addEventListener('keydown', gesture, { once: true, capture: true });
      document.addEventListener('visibilitychange', () => this.onVisibility());
      addEventListener('pagehide', () => this.viewed());
      addEventListener('pageshow', (e) => { if (e.persisted) this.media(); });

      // The first reel is current from the start; the blog keeps its own URL until you move.
      this.cur = this.reels()[0] || null;
      if (this.cur) {
        this.cur.classList.add('is-current');
        this.dataset.current = this.cur.dataset.id;
        this.since = performance.now();
      }
      this.soundUI();
      track('aura_reels_opened', { entry: this.entry(), handle: this.cur ? this.cur.dataset.id : null });
      this.media();

      // Take over after first paint, or at the first scroll, whichever comes first.
      const go = () => { if (!this.started) { this.started = true; this.takeOver(); } };
      this.list.addEventListener('scroll', go, { once: true, passive: true });
      if ('requestIdleCallback' in window) requestIdleCallback(go, { timeout: 1500 }); else setTimeout(go, 200);
    }

    reels() { return [...this.list.children]; }
    pageOf(i) { return Math.floor(i / this.pageSize) + 1; }
    find(id) { return this.list.querySelector(`[data-id="${CSS.escape(id)}"]`); }

    entry() {
      let r = null;
      try { r = document.referrer ? new URL(document.referrer) : null; } catch (e) { /* bad referrer */ }
      const mode = this.dataset.mode === 'article' ? 'article' : 'blog';
      if (!r || (r.origin === location.origin && (r.pathname === this.base || r.pathname.startsWith(`${this.base}/`)))) return mode;
      if (r.origin !== location.origin) return 'external';
      if (r.pathname === '/') return 'home_strip';
      if (r.pathname.includes('/products/')) return 'product_row';
      return 'internal';
    }

    /* ---------- loading ---------- */

    async takeOver() {
      try {
        const idx = await this.source.index();
        this.idx = idx;
        this.order = Array.isArray(idx.order) ? idx.order : [];
        this.pageSize = idx.pageSize || 24;
        this.total = this.order.length;
        this.pos = new Map(this.order.map((id, i) => [id, i]));
        const links = this.reels().filter((li) => li.classList.contains('reel--link'));
        const known = this.reels().filter((li) => !links.includes(li))
          .map((li) => this.pos.get(li.dataset.id)).filter((i) => i !== undefined);
        // An entry missing from a cached index is treated as the newest reel: page 1 loads below it.
        this.lo = known.length ? Math.min(...known) : 0;
        this.hi = known.length ? Math.max(...known) : -1;
        const n = this.pageOf(Math.max(this.lo, 0));
        await this.source.page(n); // fails before any DOM change
        links.forEach((li) => { this.io.unobserve(li); li.remove(); });
        this.dataset.live = '';
        if (this.pager) this.pager.hidden = true;
        await this.loadPage(n);
      } catch (e) {
        this.dataset.failed = '';
      }
    }

    loadPage(n) {
      if (!this.idx || n < 1 || n > (this.idx.pages || []).length || this.loaded.has(n)) return Promise.resolve();
      if (!this.loading.has(n)) {
        const p = this.source.page(n).then((page) => this.place(n, page))
          .catch(() => { /* retried the next time the edge comes near */ })
          .finally(() => { this.loading.delete(n); this.busy(); });
        this.loading.set(n, p);
        this.busy();
      }
      return this.loading.get(n);
    }

    busy() { this.toggleAttribute('data-loading', this.loading.size > 0 || this.inserting > 0); }

    async place(n, page) {
      if (this.loaded.has(n)) return;
      this.loaded.add(n);
      const above = [];
      const below = [];
      for (const item of (page && page.items) || []) {
        const i = this.pos.get(item.id);
        if (i === undefined) continue;
        this.items.set(item.id, item);
        if (this.find(item.id)) continue; // already on the page (server-rendered)
        if (i < this.lo) above.push([i, item]); else if (i > this.hi) below.push([i, item]);
      }
      below.sort((a, b) => a[0] - b[0]);
      above.sort((a, b) => a[0] - b[0]);
      if (below.length) {
        const nodes = below.map(([, item]) => this.render(item));
        this.list.append(...nodes);
        nodes.forEach((li) => this.io.observe(li));
        this.hi = below[below.length - 1][0];
      }
      if (above.length) {
        this.lo = above[0][0];
        await this.insertAbove(above.map(([, item]) => this.render(item)));
      }
      this.after();
    }

    /* Insert newer reels above without moving the current one. */
    insertAbove(nodes) {
      this.inserting += 1;
      this.busy();
      const run = this.chain.then(async () => {
        for (;;) {
          await this.idle();
          await frame();
          if (!this.scrolling) break;
        }
        const list = this.list;
        const h0 = list.scrollHeight;
        const t0 = list.scrollTop;
        list.prepend(...nodes);
        list.scrollTop = t0 + (list.scrollHeight - h0);
        nodes.forEach((li) => this.io.observe(li));
      }).finally(() => { this.inserting -= 1; this.busy(); });
      this.chain = run.catch(() => {});
      return run;
    }

    onScroll() {
      if (!this.scrolling) { this.scrolling = true; this.dataset.scrolling = ''; }
      clearTimeout(this.quiet);
      // Without scrollend, 150 ms with no scroll events counts as idle. With it, this is only a safety net.
      this.quiet = setTimeout(() => this.settle(), HAS_SCROLLEND ? 600 : 150);
    }

    settle() {
      clearTimeout(this.quiet);
      this.scrolling = false;
      delete this.dataset.scrolling;
      this.updateEnd();
      const q = this.idleQ;
      this.idleQ = [];
      q.forEach((resolve) => resolve());
    }

    idle() { return this.scrolling ? new Promise((resolve) => this.idleQ.push(resolve)) : Promise.resolve(); }

    /* Desktop wheel outside the video: scroll the post while it can, else the page. */
    onWheel(e) {
      if (!DESKTOP.matches || e.ctrlKey || this.dataset.view === 'grid' || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      const t = e.target;
      if (!t.closest || t.closest('.reel__media')) return;
      const post = t.closest('.reel__post');
      if (post && (e.deltaY > 0 ? post.scrollTop + post.clientHeight < post.scrollHeight - 1 : post.scrollTop > 0)) return;
      e.preventDefault();
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? innerHeight : 1;
      window.scrollBy({ top: e.deltaY * unit, behavior: 'instant' });
    }

    /* ---------- the current reel ---------- */

    setCurrent(li) {
      if (this.cur) {
        this.viewed();
        this.cur.classList.remove('is-current');
      }
      this.cur = li;
      li.classList.add('is-current');
      this.since = performance.now();
      this.userPaused = false;
      this.dataset.current = li.dataset.id;
      const url = li.dataset.url;
      if (url && `${location.pathname}${location.search}` !== url) history.replaceState(history.state, '', url);
      if (li.dataset.title) document.title = `${li.dataset.title}${this.dataset.titleSuffix || ''}`;
      this.after();
    }

    viewed() {
      if (!this.cur || !this.since) return;
      const i = this.pos ? this.pos.get(this.cur.dataset.id) : undefined;
      track('aura_reel_viewed', {
        handle: this.cur.dataset.id,
        position: (i === undefined ? this.reels().indexOf(this.cur) : i) + 1,
        seconds: Math.round((performance.now() - this.since) / 100) / 10
      });
      this.since = 0;
    }

    /* After any move or DOM change: memory window, paging, media, end state. */
    after() {
      if (!this.cur) return;
      const all = this.reels();
      const c = all.indexOf(this.cur);
      if (this.dataset.view !== 'grid') {
        all.forEach((li, i) => {
          const far = Math.abs(i - c) > KEEP;
          if (far && !li.classList.contains('reel--placeholder')) this.park(li);
          else if (!far && li.classList.contains('reel--placeholder')) this.unpark(li);
        });
        if (this.idx) {
          if (all.length - 1 - c <= NEAR && this.hi < this.total - 1) this.loadPage(this.pageOf(this.hi + 1));
          if (c <= NEAR && this.lo > 0) this.loadPage(this.pageOf(this.lo - 1));
        }
      }
      this.media();
      this.updateEnd();
    }

    park(li) {
      const ph = document.createElement('li');
      ph.className = 'reel reel--placeholder';
      ph.setAttribute('aria-hidden', 'true');
      ['id', 'url', 'title'].forEach((k) => { if (li.dataset[k]) ph.dataset[k] = li.dataset[k]; });
      if (!li.jsCard) this.parked.set(li.dataset.id, li); // server cards keep their node (their post isn't in the JSON)
      this.stop(li, true);
      this.io.unobserve(li);
      li.replaceWith(ph);
      this.io.observe(ph);
    }

    unpark(ph) {
      const id = ph.dataset.id;
      const node = this.parked.get(id) || (this.items.has(id) ? this.render(this.items.get(id)) : null);
      if (!node) return;
      this.parked.delete(id);
      this.io.unobserve(ph);
      ph.replaceWith(node);
      this.io.observe(node);
      if (this.cur === ph) { this.cur = node; node.classList.add('is-current'); }
    }

    updateEnd() {
      const l = this.list;
      const atEnd = l.scrollTop > 0 && l.scrollTop + l.clientHeight >= l.scrollHeight - 2;
      const noMore = !('live' in this.dataset) || this.hi >= this.total - 1;
      this.toggleAttribute('data-end', atEnd && noMore);
    }

    /* ---------- video ---------- */

    media() {
      const all = this.reels();
      const c = all.indexOf(this.cur);
      const grid = this.dataset.view === 'grid';
      all.forEach((li, i) => {
        const v = li.querySelector('.reel__video');
        if (!v) return;
        if (!grid && Math.abs(i - c) <= 1) {
          if (!v.getAttribute('src') && v.dataset.src) {
            v.src = v.dataset.src;
            const t = v.querySelector('track');
            if (t && t.dataset.src) t.src = t.dataset.src;
          }
          v.preload = this.lite ? 'none' : (i === c && this.auto ? 'auto' : 'metadata');
          if (i !== c && !v.paused) v.pause();
        } else {
          this.stop(li, true);
        }
      });
      if (!grid && this.cur) {
        this.captions(this.cur);
        if (this.auto && !this.userPaused && !document.hidden) this.play(this.cur);
      }
    }

    stop(li, unload) {
      const v = li.querySelector('.reel__video');
      if (!v) return;
      if (!v.paused) v.pause();
      if (unload && v.getAttribute('src')) {
        v.removeAttribute('src');
        const t = v.querySelector('track');
        if (t) t.removeAttribute('src');
        v.load();
        li.classList.remove('is-playing', 'has-played');
      }
    }

    wantsSound(li) { return this.sound && this.gesture && !li.hasAttribute('data-muted'); }

    play(li) {
      const v = li.querySelector('.reel__video');
      if (!v || !v.getAttribute('src')) return;
      v.muted = !this.wantsSound(li);
      this.captions(li);
      const p = v.play();
      if (p && p.catch) {
        p.catch(() => {
          if (v.muted) return;
          // Sound refused (iOS can want a gesture per element): play muted, show sound as off.
          v.muted = true;
          this.sound = false;
          this.soundUI();
          this.captions(li);
          v.play().catch(() => {});
        });
      }
    }

    toggle(li) {
      if (li !== this.cur) return;
      const v = li.querySelector('.reel__video');
      if (!v) return;
      this.gesture = true;
      if (v.paused) {
        this.userPaused = false;
        if (!v.getAttribute('src')) this.media();
        this.play(li);
      } else {
        this.userPaused = true;
        v.pause();
      }
    }

    mark(e, playing) {
      const li = e.target.closest && e.target.closest('.reel');
      if (!li) return;
      li.classList.toggle('is-playing', playing);
      if (playing) li.classList.add('has-played');
      const tap = li.querySelector('.reel__tap');
      if (tap) tap.setAttribute('aria-label', playing ? this.t.pause || '' : this.t.play || '');
    }

    applySound() {
      if (!this.cur) return;
      const v = this.cur.querySelector('.reel__video');
      if (!v) return;
      v.muted = !this.wantsSound(this.cur);
      this.captions(this.cur);
      if (!v.paused && !v.muted) v.play().catch(() => { v.muted = true; this.sound = false; this.soundUI(); });
    }

    soundUI() {
      this.querySelectorAll('[data-act="sound"]').forEach((b) => {
        b.setAttribute('aria-pressed', String(this.sound));
        b.setAttribute('aria-label', (this.sound ? this.t.sound_off : this.t.sound_on) || '');
      });
    }

    captions(li) {
      const v = li.querySelector('.reel__video');
      const track0 = v && v.textTracks && v.textTracks[0];
      if (!track0) return;
      const on = this.cc === null ? v.muted : this.cc;
      track0.mode = on ? 'showing' : 'hidden';
      const b = li.querySelector('[data-act="cc"]');
      if (b) {
        b.setAttribute('aria-pressed', String(on));
        b.setAttribute('aria-label', (on ? this.t.captions_off : this.t.captions_on) || '');
      }
    }

    onVisibility() {
      if (!this.cur) return;
      const v = this.cur.querySelector('.reel__video');
      if (document.hidden) {
        this.viewed();
        if (v && !v.paused) v.pause();
      } else {
        this.since = performance.now();
        if (this.auto && !this.userPaused) this.play(this.cur);
      }
    }

    /* ---------- interaction ---------- */

    onClick(e) {
      const el = e.target.closest('[data-act], a[href]');
      if (!el || !this.contains(el)) return;
      const li = el.closest('.reel');
      const act = el.dataset.act;
      if (act === 'play') {
        e.preventDefault();
        if (this.dataset.view === 'grid') this.toFeed(li);
        else this.toggle(li);
      } else if (act === 'sound') {
        this.gesture = true;
        this.sound = !this.sound;
        store.set(SOUND_KEY, this.sound ? '1' : '0');
        this.soundUI();
        this.applySound();
        if (this.sound) track('aura_reel_unmuted', { handle: li.dataset.id });
      } else if (act === 'cc') {
        const v = li.querySelector('.reel__video');
        this.cc = !(this.cc === null ? v.muted : this.cc);
        this.captions(li);
      } else if (act === 'share') {
        this.share(li);
      } else if (act === 'grid') {
        this.view(this.dataset.view === 'grid' ? 'feed' : 'grid');
      } else if (el.hasAttribute('data-product')) {
        track('aura_reel_product_clicked', { handle: li && li.dataset.id, product_handle: el.dataset.product });
      } else {
        this.intercept(e, el);
      }
    }

    async share(li) {
      const url = new URL(li.dataset.url, location.origin).href;
      if (navigator.share) {
        try {
          await navigator.share({ title: li.dataset.title, url });
          track('aura_reel_shared', { handle: li.dataset.id, method: 'share_api' });
        } catch (e) { /* cancelled */ }
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
        if (window.aura) window.aura.toast(this.t.copied);
        track('aura_reel_shared', { handle: li.dataset.id, method: 'copy' });
      } catch (e) { /* no clipboard: nothing to report */ }
    }

    view(mode) {
      const grid = mode === 'grid';
      this.toggleAttribute('data-view', grid);
      if (grid) this.dataset.view = 'grid';
      const b = this.querySelector('.reel-feed__view');
      if (b) {
        b.setAttribute('aria-pressed', String(grid));
        b.setAttribute('aria-label', (grid ? this.t.feed_view : this.t.grid_view) || '');
      }
      if (grid) this.reels().forEach((li) => { if (li.classList.contains('reel--placeholder')) this.unpark(li); });
      this.media();
      if (!grid) {
        // Every reel intersected the grid, so the observer won't fire again by itself: start it fresh.
        this.io.disconnect();
        this.reels().forEach((li) => this.io.observe(li));
        this.after();
      }
      track('aura_reel_grid_toggled', { to: grid ? 'grid' : 'feed' });
    }

    toFeed(li) {
      this.view('feed');
      window.scrollTo(0, 0);
      this.list.scrollTop = li.offsetTop;
    }

    step(d) {
      const all = this.reels();
      const target = all[all.indexOf(this.cur) + d];
      if (target) this.scrollToReel(target);
    }

    scrollToReel(li) {
      const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.list.scrollTo({ top: li.offsetTop, behavior: smooth ? 'smooth' : 'auto' });
    }

    onKey(e) {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || this.dataset.view === 'grid' || !this.cur) return;
      const t = e.target;
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || '') || t.isContentEditable || document.querySelector('dialog[open]')) return;
      const k = e.key;
      if (k === 'ArrowDown' || k === 'j') this.step(1);
      else if (k === 'ArrowUp' || k === 'k') this.step(-1);
      else if (k === ' ' && !(t.closest && t.closest('button, a, summary, input'))) this.toggle(this.cur);
      else if (k === 'm') { const b = this.cur.querySelector('[data-act="sound"]'); if (b) b.click(); }
      else if (k === 'c') { const b = this.cur.querySelector('[data-act="cc"]'); if (b) b.click(); }
      else return;
      e.preventDefault();
    }

    /* Links to another reel or to ?page=n scroll inside the feed instead of loading a page. */
    intercept(e, a) {
      if (!('live' in this.dataset) || e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || a.target === '_blank') return;
      const u = new URL(a.href, location.href);
      if (u.origin !== location.origin) return;
      let i;
      if (u.pathname.startsWith(`${this.base}/`)) i = this.pos.get(decodeURIComponent(u.pathname.slice(this.base.length + 1)));
      else if (u.pathname === this.base && u.searchParams.has('page')) i = (Math.max(1, parseInt(u.searchParams.get('page'), 10) || 1) - 1) * SERVER_PAGE;
      if (i === undefined || i >= this.total) return;
      e.preventDefault();
      this.go(i);
    }

    async go(i) {
      for (let lo = -1; i < this.lo && lo !== this.lo;) { lo = this.lo; await this.loadPage(this.pageOf(this.lo - 1)); }
      for (let hi = -1; i > this.hi && hi !== this.hi;) { hi = this.hi; await this.loadPage(this.pageOf(this.hi + 1)); }
      await this.chain;
      const li = this.find(this.order[i]);
      if (li) this.scrollToReel(li);
    }

    /* ---------- rendering a JSON item into the shared card ---------- */

    render(item) {
      const li = this.tpl.content.firstElementChild.cloneNode(true);
      const id = item.id;
      const slot = (name) => li.querySelector(`[data-slot="${name}"]`);
      const video = item.video || {};
      li.jsCard = true;
      li.id = `reel-${id}`;
      li.dataset.id = id;
      li.dataset.url = item.url;
      li.dataset.title = item.title || '';
      li.toggleAttribute('data-muted', !!item.muted);
      li.toggleAttribute('data-captions', !!video.captions);
      li.querySelector('.reel__inner').setAttribute('aria-labelledby', `reel-${id}-t`);

      const img = slot('poster');
      img.src = (innerWidth < 480 && item.poster360) || item.poster || item.poster360 || '';
      img.alt = item.alt || '';
      const v = slot('video');
      v.dataset.src = video.src || '';
      const tr = v.querySelector('track');
      if (video.captions && tr) tr.dataset.src = video.captions; else if (tr) tr.remove();

      const title = slot('title');
      title.id = `reel-${id}-t`;
      title.textContent = item.title || '';
      slot('dek').textContent = item.dek || '';

      const proto = slot('product');
      const ul = proto.parentNode;
      proto.remove();
      proto.removeAttribute('data-slot');
      (item.products || []).slice(0, 3).forEach((p) => {
        const row = proto.cloneNode(true);
        const a = row.firstElementChild;
        a.href = p.url || `/products/${p.handle}`;
        a.dataset.product = p.handle;
        a.textContent = p.title || p.handle;
        ul.append(row);
      });

      if (item.muted) slot('sound').remove();
      if (!video.captions) slot('cc').remove();
      if (item.igUrl) slot('ig-btn').href = item.igUrl; else slot('ig-btn').remove();

      slot('post').id = `reel-${id}-post`;
      slot('body').replaceChildren(clean(item.bodyHtml));
      this.soundUIFor(li);
      return li;
    }

    soundUIFor(li) {
      const b = li.querySelector('[data-act="sound"]');
      if (b) {
        b.setAttribute('aria-pressed', String(this.sound));
        b.setAttribute('aria-label', (this.sound ? this.t.sound_off : this.t.sound_on) || '');
      }
    }
  }

  customElements.define('aura-feed', AuraFeed);
})();
